/**
 * Break Tracking Service Base Class
 * Platform-agnostic implementation - no API client imports!
 * Both web and mobile versions instantiate this with their respective API clients
 */
export class BreakTrackingService {
    constructor(client) {
        this.apiClient = client;
    }
    // Break Type Operations
    /**
     * List break types for an account
     */
    async listBreakTypes(accountId, includeInactive = false) {
        const params = includeInactive ? { include_inactive: true } : {};
        return this.apiClient.get(`/accounts/${accountId}/break-types`, params);
    }
    /**
     * Create a new break type
     */
    async createBreakType(accountId, request) {
        return this.apiClient.post(`/accounts/${accountId}/break-types`, request);
    }
    /**
     * Get a break type by ID
     */
    async getBreakType(breakTypeId) {
        return this.apiClient.get(`/break-types/${breakTypeId}`);
    }
    /**
     * Update a break type
     */
    async updateBreakType(breakTypeId, request) {
        return this.apiClient.put(`/break-types/${breakTypeId}`, request);
    }
    /**
     * Delete a break type (soft delete)
     */
    async deleteBreakType(breakTypeId) {
        return this.apiClient.delete(`/break-types/${breakTypeId}`);
    }
    // Break Period Operations
    /**
     * Start a break for a work session
     */
    async startBreak(sessionId, request) {
        return this.apiClient.post(`/sessions/${sessionId}/breaks/start`, request || {});
    }
    /**
     * End an active break
     */
    async endBreak(breakId, request) {
        return this.apiClient.post(`/breaks/${breakId}/end`, request || {});
    }
    /**
     * Cancel an active break
     */
    async cancelBreak(breakId) {
        return this.apiClient.post(`/breaks/${breakId}/cancel`);
    }
    /**
     * Get breaks for a session
     */
    async getBreaksBySession(sessionId) {
        return this.apiClient.get(`/sessions/${sessionId}/breaks`);
    }
    /**
     * Get a session with all its breaks
     */
    async getSessionWithBreaks(sessionId) {
        return this.apiClient.get(`/sessions/${sessionId}/with-breaks`);
    }
    // Timeline Operations
    /**
     * Get day timeline for a user
     */
    async getDayTimeline(userId, date, timezone) {
        const params = {};
        if (date)
            params.date = date;
        if (timezone)
            params.tz = timezone; // the server builds the day window in this zone
        return this.apiClient.get(`/users/${userId}/timeline`, params);
    }
    // Admin Time Editing Operations
    // Uses /time-admin endpoints which require work_sessions:edit permission
    // (not system admin privileges)
    /**
     * Edit a work session (admin)
     */
    async editWorkSession(sessionId, request) {
        return this.apiClient.post(`/time-admin/sessions/${sessionId}/edit`, request);
    }
    /**
     * Edit a break period (admin)
     */
    async editBreakPeriod(breakId, request) {
        return this.apiClient.post(`/time-admin/breaks/${breakId}/edit`, request);
    }
    /**
     * Get audit history for an entity
     */
    async getAuditHistory(entityType, entityId) {
        return this.apiClient.get(`/time-admin/audit/${entityType}/${entityId}`);
    }
    /**
     * Get audit logs with filters
     */
    async getAuditLogs(filters) {
        return this.apiClient.get('/time-admin/audit', filters);
    }
    /**
     * Inject a break period into a session (admin)
     */
    async injectBreakPeriod(sessionId, request) {
        return this.apiClient.post(`/sessions/${sessionId}/breaks/inject`, {
            start_time: request.startTime,
            end_time: request.endTime,
            reason: request.reason,
        });
    }
    /**
     * Create a new completed work session (admin)
     * Uses /time-admin endpoint which requires work_sessions:edit permission
     */
    async createAdminSession(request) {
        return this.apiClient.post('/time-admin/sessions/create', request);
    }
    /**
     * Batch edit a work session (admin)
     * Applies multiple changes (session times, break edits, new breaks, deletions) in a single API call
     * Uses /time-admin endpoint which requires work_sessions:edit permission (not system admin)
     */
    async batchEditSession(sessionId, request) {
        return this.apiClient.post(`/time-admin/sessions/${sessionId}/batch-edit`, {
            new_start_time: request.newStartTime,
            new_end_time: request.newEndTime,
            break_edits: request.breakEdits?.map((be) => ({
                break_id: be.breakId,
                new_start_time: be.newStartTime,
                new_end_time: be.newEndTime,
            })),
            new_breaks: request.newBreaks?.map((nb) => ({
                break_type_id: nb.breakTypeId,
                start_time: nb.startTime,
                end_time: nb.endTime,
            })),
            delete_break_ids: request.deleteBreakIds,
            reason: request.reason,
        });
    }
    /**
     * Soft-delete a work session
     * Uses /time-admin endpoint which requires work_sessions:delete permission
     */
    async deleteWorkSession(sessionId, reason) {
        return this.apiClient.post(`/time-admin/sessions/${sessionId}/delete`, { reason });
    }
    /**
     * Restore a soft-deleted work session
     * Uses /time-admin endpoint which requires work_sessions:delete permission
     */
    async restoreWorkSession(sessionId, reason) {
        return this.apiClient.post(`/time-admin/sessions/${sessionId}/restore`, { reason });
    }
    /**
     * Force-end a RUNNING work session (admin remedy for stuck clocks).
     * Requires work_sessions:update. endTime defaults to now on the server.
     */
    async forceEndSession(sessionId, reason, endTime) {
        return this.apiClient.post(`/time-admin/sessions/${sessionId}/end`, {
            reason,
            ...(endTime ? { end_time: endTime } : {}),
        });
    }
}
